import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, BarChart3, Wrench, TrendingUp, ArrowLeftRight, Users, Database, Crown, Video, Palette, Headset, Send, Webhook, DollarSign, Music4, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AdminReports from "@/pages/admin/AdminReports";
import AdminGrowth from "@/pages/admin/AdminGrowth";
import AdminPix from "@/pages/admin/AdminPix";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminInstrumentVideos from "@/pages/admin/AdminInstrumentVideos";
import AdminDatabase from "@/pages/admin/AdminDatabase";
import AdminIdentity from "@/pages/admin/AdminIdentity";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminPayouts from "@/pages/admin/AdminPayouts";
import AdminAsaas from "@/pages/admin/AdminAsaas";
import AdminMinCache from "@/pages/admin/AdminMinCache";
import AdminMaintenance from "@/pages/admin/AdminMaintenance";
import AdminMusicBank from "@/pages/admin/AdminMusicBank";
import AdminWebSearch from "@/pages/admin/ws/AdminWebSearch";
import ManualVivo from "@/pages/admin/manual/ManualVivo";

const ADMIN_ROLES = ["suporte", "financeiro", "admin", "super_admin"];
const ROLE_LABEL = { suporte: "Suporte", financeiro: "Financeiro", admin: "Administrador", super_admin: "Super Admin" };

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("relatorios");

  useEffect(() => { if (user && !ADMIN_ROLES.includes(user.role)) navigate("/"); }, [user]); // eslint-disable-line
  if (user && !ADMIN_ROLES.includes(user.role)) return null;

  const canReports = user?.permissions?.includes("reports:read");
  const canFinance = user?.permissions?.includes("finance:read");
  const canUsers = user?.permissions?.includes("users:read");
  const canDb = user?.permissions?.includes("db:read");
  const canReset = user?.permissions?.includes("data:reset");
  const canPlans = user?.permissions?.includes("plans:read");
  const isSuper = user?.permissions?.includes("system:admin");

  const sections = [
    canReports && { id: "relatorios", label: "Relatórios", icon: BarChart3 },
    canReports && { id: "crescimento", label: "Crescimento", icon: TrendingUp },
    canFinance && { id: "pix", label: "Transações Pix", icon: ArrowLeftRight },
    canFinance && { id: "repasses", label: "Repasses Pix", icon: Send },
    canPlans && { id: "planos", label: "Planos & Assinaturas", icon: Crown },
    isSuper && { id: "cachemin", label: "Cachê mínimo", icon: DollarSign },
    canUsers && { id: "usuarios", label: "Usuários", icon: Users },
    canUsers && { id: "atendimento", label: "Atendimento", icon: Headset },
    canUsers && { id: "videos", label: "Vídeos & Níveis", icon: Video },
    canDb && { id: "musicas", label: "Banco de Músicas", icon: Music4 },
    canDb && { id: "buscaavancada", label: "Busca na Web Avançada", icon: Music4 },
    canDb && { id: "banco", label: "Banco de Dados", icon: Database },
    canReset && { id: "manutencao", label: "Manutenção", icon: Wrench },
    isSuper && { id: "identidade", label: "Identidade & Landing", icon: Palette },
    isSuper && { id: "asaas", label: "Asaas / Webhooks", icon: Webhook },
    isSuper && { id: "manual", label: "Manual Vivo", icon: BookOpen },
  ].filter(Boolean);
  const activeTab = sections.some((s) => s.id === tab) ? tab : (sections[0]?.id);

  return (
    <div className={`${["buscaavancada", "manual"].includes(activeTab) ? "max-w-[1500px]" : "max-w-6xl"} mx-auto space-y-6`} data-testid="admin-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3"><Shield className="h-8 w-8 text-[#FF4D00]" />Administração</h1>
          <p className="text-muted-foreground mt-1">Painel administrativo do Mí Chamou.</p>
        </div>
        <span data-testid="admin-role-badge" className="text-xs px-2.5 py-1 rounded-full border border-[#FF4D00]/40 bg-[#FF4D00]/10 text-[#FF4D00] font-semibold">{ROLE_LABEL[user?.role] || user?.role}</span>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="admin-tabs">
        {sections.map((s) => (
          <button key={s.id} data-testid={`admin-tab-${s.id}`} onClick={() => setTab(s.id)}
            className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${activeTab === s.id ? "bg-[#FF4D00] text-white border-[#FF4D00] shadow-[0_0_18px_rgba(255,79,0,0.3)]" : "bg-card text-muted-foreground border-white/10 hover:text-foreground hover:border-white/20"}`}>
            <s.icon className="h-4 w-4" />{s.label}
          </button>
        ))}
      </div>

      {activeTab === "relatorios" && canReports && <AdminReports />}
      {activeTab === "crescimento" && canReports && <AdminGrowth />}
      {activeTab === "pix" && canFinance && <AdminPix />}
      {activeTab === "repasses" && canFinance && <AdminPayouts />}
      {activeTab === "planos" && canPlans && <AdminPlans />}
      {activeTab === "cachemin" && isSuper && <AdminMinCache />}
      {activeTab === "usuarios" && canUsers && <AdminUsers />}
      {activeTab === "atendimento" && canUsers && <AdminTickets />}
      {activeTab === "videos" && canUsers && <AdminInstrumentVideos />}
      {activeTab === "banco" && canDb && <AdminDatabase />}
      {activeTab === "musicas" && canDb && <AdminMusicBank />}
      {activeTab === "buscaavancada" && canDb && <AdminWebSearch />}
      {activeTab === "manual" && isSuper && <ManualVivo />}
      {activeTab === "manutencao" && canReset && <AdminMaintenance />}
      {activeTab === "identidade" && isSuper && <AdminIdentity />}
      {activeTab === "asaas" && isSuper && <AdminAsaas />}
    </div>
  );
}
